import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { FiChevronDown } from "react-icons/fi";

import logo from '../../assets/img/logoblanco.png'
import ampiLogo from '../../assets/img/ampi-logo.png'
import { useUser } from '../../context/UserContext';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth, db } from '../../firebaseConfig';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import LanguageToggle from '../LanguageToggle';
import { getFirebaseErrorMessage } from '../../utils/errorMessages';

export default function Navbar({transparent}:{transparent:any}) {
    const { t } = useTranslation();
    const [activeMenu, setActiveMenu] = useState<{[key: string]: { [key: string]: boolean };}>({});
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [toggle, setIsToggle] = useState<boolean>(false);
    const [login, setLogin] = useState<boolean>(false);
    const [userMenu, setUserMenu] = useState<boolean>(false);
    const { setUser, user, logout, loading: userLoading } = useUser();
    const navigate = useNavigate();
    const [justLoggedIn, setJustLoggedIn] = useState(false);

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [resetMessage, setResetMessage] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    let[scroll,setScroll] = useState<boolean>(false)

    const location = useLocation();
    const current = location.pathname

    // Cargar solo email guardado (Sprint 6 F1-008: validar formato antes de prellenar)
    useEffect(() => {
        const savedEmail = localStorage.getItem('ampi_saved_email');
        localStorage.removeItem('ampi_saved_password');
        // Regex RFC-lite: evita XSS si el valor fue manipulado en DevTools.
        const emailRe = /^[^\s<>"'()]+@[^\s<>"'()]+\.[^\s<>"'()]+$/;
        if (savedEmail && savedEmail.length <= 120 && emailRe.test(savedEmail)) {
            setEmail(savedEmail);
            setRememberMe(true);
        } else if (savedEmail) {
            localStorage.removeItem('ampi_saved_email');
        }
    }, []);

    // Escucha global para abrir el modal de login desde cualquier página (ej. guards de inscripción)
    useEffect(() => {
        const abrir = () => setLogin(true);
        window.addEventListener('ampi:open-login', abrir);
        return () => window.removeEventListener('ampi:open-login', abrir);
    }, []);

    // Redirigir a seleccionar-rol si el usuario tiene permisos de comité después del login
    useEffect(() => {
        if (justLoggedIn && !userLoading && user) {
            setJustLoggedIn(false);
            if (user.esAdminComite) {
                navigate('/seleccionar-rol');
            }
        }
    }, [justLoggedIn, userLoading, user, navigate]);

    const handleLogin = async () => {
        if (user) {
          setError(t('auth.alreadyLoggedIn'));
          return;
        }
        try {
          await signInWithEmailAndPassword(auth, email, password);
          if (rememberMe) {
            localStorage.setItem('ampi_saved_email', email);
          } else {
            localStorage.removeItem('ampi_saved_email');
          }
          setUser(null);
          setLogin(false);
          setEmail('');
          setPassword('');
          setError('');
          setJustLoggedIn(true);
        } catch (err: any) {
          console.error(err.message);
          setError(getFirebaseErrorMessage(err));
        }
      };

      const handleForgotPassword = async () => {
        // Sprint 0 (F2-002): eliminada verificación previa de existencia del email.
        // Revelar si un correo está registrado permite enumeración de usuarios.
        // Ahora siempre devolvemos el mismo mensaje, independientemente de si existe o no.
        if (!resetEmail) {
          setResetMessage('Por favor, ingresa tu correo electrónico.');
          return;
        }
        const GENERIC_MSG = 'Si existe una cuenta con este correo, recibirás instrucciones para restablecer tu contraseña.';
        try {
          await sendPasswordResetEmail(auth, resetEmail);
        } catch (err: any) {
          // Silenciar errores de "user-not-found" / "invalid-email" para no filtrar existencia.
          // Otros errores (red, rate limit) se registran internamente.
          if (err?.code && err.code !== 'auth/user-not-found' && err.code !== 'auth/invalid-email') {
            if (process.env.NODE_ENV === 'development') console.error(err);
          }
        }
        setResetMessage(GENERIC_MSG);
      };

    const handleMouseEnter = (menu: string, submenu?: string) => {
        setActiveMenu((prev) => ({
          ...prev,
          [menu]: { ...prev[menu], [submenu || 'main']: true },
        }));
      };

      const handleMouseLeave = (menu: string, submenu?: string) => {
        setActiveMenu((prev) => ({
          ...prev,
          [menu]: { ...prev[menu], [submenu || 'main']: false },
        }));
      };

    useEffect(()=>{
        window.scrollTo(0,0)
        const handlerScroll = () =>{
            if(window.scrollY > 50){ setScroll(true) } else { setScroll(false) }
          };
        const handleResize = () => { setWindowWidth(window.innerWidth); };
        window.addEventListener('scroll',handlerScroll)
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('scroll',handlerScroll)
            window.removeEventListener('resize', handleResize);
          };
    },[windowWidth])

    const isAdmin = () => user?.rol === 'ADMIN';
    const isAdminComite = () => user?.rol === 'ADMIN_COMITE';
    const isAsociado = () => user?.rol === 'ASOCIADO';
    const isAfiliado = () => user?.rol === 'AFILIADO';

    // Opciones del desplegable de usuario (mismo contenido que user-nav.tsx para consistencia).
    const userMenuLinks = (
      <>
        {isAdmin() && (
          <Link to="/admin-dashboard" onClick={() => setUserMenu(false)}><i className="fa-solid fa-gauge"></i> Panel Administrativo</Link>
        )}
        {isAdminComite() && (
          <>
            <Link to="/admin-comite/dashboard" onClick={() => setUserMenu(false)}><i className="fa-solid fa-gauge"></i> Panel de Comité</Link>
            <Link to="/admin-talleres" onClick={() => setUserMenu(false)}><i className="fa-solid fa-calendar-days"></i> Listado de Talleres</Link>
            <Link to="/admin-create-taller" onClick={() => setUserMenu(false)}><i className="fa-solid fa-plus"></i> Crear Evento</Link>
            <Link to="/escanear-asistencia" onClick={() => setUserMenu(false)}><i className="fa-solid fa-qrcode"></i> Escanear Asistencia</Link>
          </>
        )}
        {(isAsociado() || isAfiliado()) && (
          <Link to="/my-profile" onClick={() => setUserMenu(false)}><i className="fa-solid fa-address-card"></i> Mi Perfil</Link>
        )}
        {(isAsociado() || isAfiliado()) && (
          <Link to="/my-property" onClick={() => setUserMenu(false)}><i className="fa-solid fa-building-circle-check"></i> Mis Propiedades</Link>
        )}
        <Link to="/change-password" onClick={() => setUserMenu(false)}><i className="fa-solid fa-unlock"></i> Cambiar Contraseña</Link>
        {(isAsociado() || isAfiliado()) && (
          <Link to="/mis-cursos" onClick={() => setUserMenu(false)}><i className="fa-solid fa-graduation-cap"></i> Mis Cursos</Link>
        )}
        <button
          onClick={logout}
          className="dropdown-item logout-btn-red"
          style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', padding: '0.5rem 1rem' }}
        >
          <i className="fa-solid fa-right-from-bracket"></i> <span>Cerrar Sesión</span>
        </button>
      </>
    );

  return (
    <>
        {/* ========== CONVENTIONAL NAVBAR ========== */}
        <div className={`header ${scroll ? 'header-fixed' : ''} ${transparent ? 'header-transparent dark' : 'header-light head-shadow'}`}>
            <div className="container">
                <nav id="navigation " className={windowWidth > 991 ? "navigation navigation-landscape" : "navigation navigation-portrait"}>
                    <div className="nav-header" style={{lineHeight:'0'}}>
                        <Link className="nav-brand text-logo" to="/">
                            <img src={transparent && !scroll ? logo : ampiLogo} alt="Logo AMPI Riviera Nayarit" />
                        </Link>
                        <button className="nav-toggle" onClick={()=>setIsToggle(!toggle)} aria-label="Abrir menú de navegación" aria-expanded={toggle}></button>
                        <div className="mobile_nav">
                            <ul>
                                {!user && (
                                    <li>
                                        <Link to="#" onClick={()=>setLogin(!login)} style={{ fontSize: '1.2rem' }}>
                                            <i className={`fa-solid fa-user ${transparent && !scroll ? 'text-white' : 'text-dark'}`}></i>
                                        </Link>
                                    </li>
                                )}
                                {user && (
                                    <li>
                                        <div className="btn-group account-drop">
                                            <button
                                                type="button"
                                                className="btn p-0 border-0 bg-transparent"
                                                onClick={() => setUserMenu(!userMenu)}
                                                aria-label="Menú de usuario"
                                                aria-expanded={userMenu}
                                                style={{ fontSize: '1.2rem', lineHeight: 0, boxShadow: 'none' }}
                                            >
                                                <i className={`fa-solid fa-circle-user ${transparent && !scroll ? 'text-white' : 'text-dark'}`}></i>
                                            </button>
                                            <div
                                                className="dropdown-menu pull-right animated flipInX"
                                                style={{ display: userMenu ? 'block' : 'none', right: 0, zIndex: 20050 }}
                                            >
                                                {userMenuLinks}
                                            </div>
                                        </div>
                                    </li>
                                )}
                            </ul>
                        </div>
                    </div>
                    <div className={`nav-menus-wrapper  ${toggle ? 'nav-menus-wrapper-open' : ''}`} style={{transitionProperty:toggle ? 'none' : 'left'}}>
                        <button className="nav-menus-wrapper-close-button" onClick={()=>setIsToggle(!toggle)} aria-label="Cerrar menú de navegación">✕</button>
                        <ul className="nav-menu" style={{float:'right'}}>
                            <li className={`${['/','/home-2','/home-3','/home-4','/home-5','/home-6','/home-7','/home-8','/home-9','/home-10','/home-11','/video','/map'].includes(current) ? 'active' : ''} ${activeMenu['home']?.main ? 'show' : ''}`} onMouseEnter={() => handleMouseEnter('home')} onMouseLeave={() => handleMouseLeave('home')}><Link to="/">{t('navigation.home')}</Link>
                            </li>

                            <li className={current === '/classical-layout-with-sidebar' ? 'active' : ''}>
                                <Link to="/classical-layout-with-sidebar">{t('navigation.properties')}</Link>
                            </li>

                            <li className={`${['/agents','/agent-page','/asociados','/asociado-page'].includes(current)?'active':''} ${activeMenu['members']?.main ? 'show' : ''}`} onMouseEnter={() => handleMouseEnter('members')} onMouseLeave={() => handleMouseLeave('members')}>
                                <Link to="#">{t('navigation.members')}<FiChevronDown className="submenu-indicator"/></Link>
                                <ul className="nav-dropdown nav-submenu" style={{display: activeMenu['members']?.main ? 'block' : 'none', pointerEvents: activeMenu['members']?.main ? 'auto' : 'none'}}>
                                    <li><Link to="/agents" className={current === '/agents' ? 'active' : ''}>{t('navigation.affiliates')}</Link></li>
                                    <li><Link to="/asociados" className={current === '/asociados' ? 'active' : ''}>{t('navigation.associates')}</Link></li>
                                </ul>
                            </li>

                            <li className={['/agencies','/agency-page'].includes(current)?'active':''}><Link to="/agencies">{t('agency.agencies')}</Link></li>

                            <li className={current === '/ver-taller' ? 'active' : ''}>
                                <Link to="/ver-taller">Cursos y Talleres</Link>
                            </li>

                            {user && (
                                <li className={`${['/dashboard','/my-profile','/bookmark-list','/change-password','/calificacion'].includes(current)?'active':''} ${activeMenu['account']?.main ? 'show' : ''}`} onMouseEnter={() => handleMouseEnter('account')} onMouseLeave={() => handleMouseLeave('account')}>
                                    <Link to="#">{t('navigation.myAccount')}<FiChevronDown className="submenu-indicator"/></Link>
                                    <ul className="nav-dropdown nav-submenu" style={{display: activeMenu['account']?.main ? 'block' : 'none', pointerEvents: activeMenu['account']?.main ? 'auto' : 'none'}}>
                                        <li><Link to="/dashboard" className={current === '/dashboard' ? 'active' : ''}>{t('navigation.dashboard')}</Link></li>
                                        <li><Link to="/calificacion" className={current === '/calificacion' ? 'active' : ''}>{t('navigation.addReview')}</Link></li>
                                    </ul>
                                </li>
                            )}

                            {user && (
                                <li className="d-lg-none">
                                    <Link to={user.rol === 'ADMIN' ? '/admin-dashboard' : '/dashboard'} className="fw-medium text-primary">
                                        {t('navigation.dashboard')}
                                    </Link>
                                </li>
                            )}

                            <li className="d-none d-lg-inline-block" style={{paddingTop:14}}>
                              <LanguageToggle />
                            </li>
                            {!user && (
                              <li className="d-none d-lg-inline-block">
                                <Link to="#" className="fw-medium text-muted-2" onClick={(e) => {
                                  e.preventDefault();
                                  setLogin(!login);
                                }}>
                                  <span className="svg-icon svg-icon-2hx">
                                    <svg width="22" height="22" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path opacity="0.3" d="M16.5 9C16.5 13.125 13.125 16.5 9 16.5C4.875 16.5 1.5 13.125 1.5 9C1.5 4.875 4.875 1.5 9 1.5C13.125 1.5 16.5 4.875 16.5 9Z" fill="currentColor"/>
                                      <path d="M9 16.5C10.95 16.5 12.75 15.75 14.025 14.55C13.425 12.675 11.4 11.25 9 11.25C6.6 11.25 4.575 12.675 3.975 14.55C5.25 15.75 7.05 16.5 9 16.5Z" fill="currentColor"/>
                                      <rect x="7" y="6" width="4" height="4" rx="2" fill="currentColor"/>
                                    </svg>
                                  </span>
                                </Link>
                              </li>
                            )}
                            {user && (
                              <>
                                <li className="add-listing d-none d-lg-inline-block me-3">
                                  <Link to={user.rol === 'ADMIN' ? '/admin-dashboard' : '/dashboard'} className="bg-primary">
                                    <i className="fa-solid fa-gauge me-1"></i>
                                    {t('navigation.dashboard')}
                                  </Link>
                                </li>
                                <li className="d-none d-lg-inline-block">
                                  <span className="fw-medium text-muted-2">
                                    {t('navigation.hello')}, {user.nombre?.split(' ')[0] || 'Usuario'}
                                  </span>
                                </li>
                                <li className="d-none d-lg-inline-block">
                                  <button onClick={logout} className="btn btn-outline-secondary btn-sm ms-2">
                                    {t('navigation.logout')}
                                  </button>
                                </li>
                              </>
                            )}
                        </ul>
                    </div>
                    <div className="nav-overlay-panel" style={{backgroundColor: 'rgba(0, 0, 0, 0.5)', display: toggle ?  'block' : 'none'}}></div>
                </nav>
            </div>
        </div>
        <div className="clearfix"></div>

        {/* ========== FLOATING BUTTON - SITIO OFICIAL ========== */}
        <a
            href="https://ampirivieranayarit.com/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Visitar sitio oficial AMPI"
            style={{
                position: 'fixed', bottom: 70, right: 24, zIndex: 1050,
                width: 52, height: 52, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundColor: '#1a5d4a',
                boxShadow: '0 4px 16px rgba(26,93,74,0.4)',
                color: '#fff', fontSize: 22, textDecoration: 'none',
                transition: 'transform 0.2s, box-shadow 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.1)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
        >
            <i className="fa fa-globe" aria-hidden="true"></i>
        </a>

        {/* Login modal */}
        {login &&
            <div className="modal fade show" style={{display: 'block', backgroundColor:"#0000008a"}} role="dialog" aria-modal="true" aria-labelledby="login-modal-title">
                <div className="modal-dialog modal-dialog-centered login-pop-form" role="document">
                    <div className="modal-content" id="registermodal">
                        <button className="mod-close" onClick={()=>setLogin(!login)} aria-label="Cerrar ventana de inicio de sesión" type="button">
                            <span className="svg-icon text-primary svg-icon-2hx" aria-hidden="true">
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect opacity="0.3" x="2" y="2" width="20" height="20" rx="10" fill="currentColor"></rect>
                                    <rect x="7" y="15.3137" width="12" height="2" rx="1" transform="rotate(-45 7 15.3137)" fill="currentColor"></rect>
                                    <rect x="8.41422" y="7" width="12" height="2" rx="1" transform="rotate(45 8.41422 7)" fill="currentColor"></rect>
                                </svg>
                            </span>
                        </button>
                        <div className="modal-body" style={{ padding: '2rem 2.5rem' }}>
                            <div className="text-center mb-3">
                                <img src={ampiLogo} alt="AMPI Riviera Nayarit" width={160} loading="lazy" style={{ objectFit: 'contain' }} />
                            </div>
                            <h5 id="login-modal-title" className="text-center mb-3" style={{ fontSize: '1.4rem', fontWeight: 600 }}>Iniciar Sesión</h5>
                            {error && <div className="alert alert-danger py-2 mb-3" style={{ fontSize: '0.85rem' }}><i className="fas fa-exclamation-circle me-2"></i>{error}</div>}
                            <div className="login-form">
                                <form onSubmit={(e) => { e.preventDefault(); handleLogin(); }}>
                                    <div className="mb-3">
                                        <label className="form-label mb-1" style={{ fontSize: '0.85rem' }}>Correo electronico</label>
                                        <input type="email" className="form-control" placeholder="correo@ejemplo.com" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label mb-1" style={{ fontSize: '0.85rem' }}>Contrasena</label>
                                        <div className="position-relative">
                                            <input type={showPassword ? "text" : "password"} className="form-control" placeholder="********" value={password} onChange={(e) => setPassword(e.target.value)} style={{ paddingRight: '40px' }} autoComplete="current-password" />
                                            <button
                                              type="button"
                                              onClick={() => setShowPassword(!showPassword)}
                                              style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', cursor: 'pointer', padding: '4px 8px' }}
                                              title={showPassword ? "Ocultar" : "Mostrar"}
                                            >
                                              <i className={`fa ${showPassword ? 'fa-eye-slash' : 'fa-eye'} text-muted`}></i>
                                            </button>
                                        </div>
                                    </div>

                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox" id="save-pass" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} />
                                            <label className="form-check-label" htmlFor="save-pass" style={{ fontSize: '0.85rem' }}>Recuérdame</label>
                                        </div>
                                        <button type="button" className="btn btn-link p-0" style={{ fontSize: '0.85rem' }} onClick={() => setShowForgotPassword(true)}>
                                            Olvide mi contrasena
                                        </button>
                                    </div>

                                    <button type="submit" className="btn btn-primary fw-medium full-width rounded-2 py-2">Iniciar Sesión</button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        }

        {/* Modal para recuperar contraseña */}
        {showForgotPassword &&
            <div className="modal fade show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
                <div className="modal-dialog modal-dialog-centered" role="document">
                    <div className="modal-content">
                        <div className="modal-body" style={{ padding: '2rem 2.5rem' }}>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="mb-0" style={{ fontSize: '1.3rem', fontWeight: 600 }}>Recuperar contrasena</h5>
                                <button type="button" className="btn-close" onClick={() => { setShowForgotPassword(false); setResetEmail(''); setResetMessage(''); }}></button>
                            </div>
                            <p className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>Ingresa tu correo electronico y te enviaremos un enlace para restablecer tu contrasena.</p>
                            <div className="mb-3">
                                <label className="form-label mb-1" style={{ fontSize: '0.85rem' }}>Correo electronico</label>
                                <input type="email" className="form-control" placeholder="correo@ejemplo.com" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} />
                            </div>
                            {resetMessage && (
                                <div className={`alert ${resetMessage.includes('enviado') ? 'alert-success' : 'alert-danger'} py-2 mb-3`} style={{ fontSize: '0.85rem' }}>{resetMessage}</div>
                            )}
                            <button type="button" className="btn btn-primary full-width py-2" onClick={handleForgotPassword}>Enviar enlace</button>
                            <div className="text-center mt-3">
                                <button type="button" className="btn btn-link p-0" style={{ fontSize: '0.85rem' }} onClick={() => setShowForgotPassword(false)}>Volver al login</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        }

    </>
  )
}
